import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion';
import { ThemeSystemConfig } from '../clipCreator/remotion/Components/Theme/Theme';
import {
    LayerContainer,
    SceneLayerContainer,
    BackgroundLayerContainer,
    OverlayLayerContainer,
    OverlayTransitionLayerContainer,
    TextOverlayLayerContainer,
    CaptionLayerContainer,
    BrandingLayerContainer,
    MainAudioLayerContainer,
} from './layers';
import { AspectRatio, ResolutionTier, getVideoConfigFromAspectRatio } from './AspectRatio';
import { TimelineSettings } from './TimelineSettings';
import { TimelineDefaults } from './TimelineDefaults';
import {
    SceneItem,
    BackgroundItem,
    OverlayItem,
    TextOverlayItem,
    CaptionItem,
    BrandingItem,
    AnimationConfig,
    CaptionToken,
    BackgroundItemImpl,
    OverlayItemImpl,
    TextOverlayItemImpl,
    CaptionItemImpl,
    BrandingItemImpl,
    AnimatedItem,
    SoundConfig,
    ANIMATION_TIMING_DEFAULTS
} from './types';
import { OverlayTransitionItemImpl } from './types/overlay-transition';
import { MediaObject } from './media';
import type { TransitionConfig } from './TransitionConfig';
import { Position } from './Utils';
import { parseMedia } from '@remotion/media-parser';
import { MultiTrackEffect, MultiTrackEffectType, LayerType, PerLayerBlurEffect, PerLayerStaticBlurEffect } from './effects';
// Import new class names with backward compatibility aliases  
import { MultiTrackZoomEffect, MultiTrackBlurEffect } from './effects';
// Import new container effects
import { BaseContainerEffect } from './effects/BaseContainerEffect';
import { ContainerBlurEffect } from './effects/ContainerBlurEffect';
import { ContainerZoomEffect } from './effects/ContainerZoomEffect';
import { DesignStyle } from './DesignStyle';
import { SoundPlayer } from './audio/SoundPlayer';
import { SequenceTimingHelper } from './helpers/SequenceTimingHelper';
import { TimelineDurationCalculator, ensureTimelineDuration } from './helpers/TimelineDurationHelper';
import {
    BrandingState,
    CaptionState,
    OverlayState,
    TimelineState,
    SceneState,
    Serializable,
    SerializableState,
    AnimationState,
    MediaState,
    TextStyleState,
    TextOverlayState
} from './types/serializable';
import TimelineRegistry from './TimelineRegistry';
import { validateTimelineState } from './schemas/timeline-schema';
import type { TextStyle } from './text/types';

// Type guard to check if an object has exportState method
function hasExportState<TState extends SerializableState>(obj: unknown): obj is { exportState(): TState } {
    const candidate = obj as { exportState?: unknown };
    return obj !== null &&
           typeof obj === 'object' &&
           'exportState' in obj &&
           typeof candidate.exportState === 'function';
}

function createTimelineTheme(backgroundColor: string = '#000000'): ThemeSystemConfig {
    return {
        brandConfig: {
            primaryColor: '#000000',
            secondaryColor: '#FFFFFF',
            accentColor: '#FFFFFF',
            titleFont: 'inter',
            bodyFont: 'inter',
            logoURL: '',
            titleFontOutline: 'none',
            bodyFontOutline: 'none',
            borderRadius: 0
        },
        backgroundConfig: {
            imageURL: '',
            color: backgroundColor,
            format: 'color'
        },
        frameConfig: {
            borderRadius: 0,
            padding: false,
            border: false,
            borderStyle: 'no_border'
        },
        savedCaptionConfig: {
            id: 'default',
            name: 'Default',
            type: 'trebble',
            captionConfig: {
                milisecondGrouping: 1000,
                font: 'inter',
                useDesignSystemColor: true,
                mainColor: '#FFFFFF',
                highlightColor: '#FFFFFF',
                outline: 'none',
                fontSizeRatio: 'medium',
                background: 'none',
                textAnimation: 'none',
                borderRadius: 0,
                captionCase: 'standard',
                hidePunctuation: false,
                showOneWordOnly: false,
                maxLines: 'two_lines',
                position: 'bottom center'
            }
        }
    };
}

interface LegacyTimelineTheme extends ThemeSystemConfig {
    backgroundColor?: unknown;
}

const ANIMATION_TYPES: readonly AnimationConfig['type'][] = [
    'none',
    'fade',
    'slide',
    'slide-up',
    'slide-down',
    'slide-left',
    'slide-right',
    'scale',
    'scale-up',
    'scale-down',
    'rotate',
    'bounce',
    'elastic',
    'fadeOutUp',
    'fadeInDown',
    'slideIn',
    'slideOut',
    'scaleIn',
    'scaleOut',
    'fadeIn',
    'fadeOut',
    'typewriter',
    'stagger'
];

const ANIMATION_DIRECTIONS: readonly NonNullable<AnimationConfig['direction']>[] = [
    'top',
    'bottom',
    'left',
    'right'
];

const EASING_TYPES: readonly NonNullable<AnimationConfig['timing']['easing']>[] = [
    'linear',
    'ease',
    'ease-in',
    'ease-out',
    'ease-in-out',
    'ease-in-quad',
    'ease-out-quad',
    'ease-in-out-quad',
    'ease-in-cubic',
    'ease-out-cubic',
    'ease-in-out-cubic',
    'ease-in-quart',
    'ease-out-quart',
    'ease-in-out-quart',
    'ease-in-quint',
    'ease-out-quint',
    'ease-in-out-quint',
    'ease-in-sine',
    'ease-out-sine',
    'ease-in-out-sine',
    'ease-in-expo',
    'ease-out-expo',
    'ease-in-out-expo',
    'ease-in-circ',
    'ease-out-circ',
    'ease-in-out-circ',
    'ease-in-back',
    'ease-out-back',
    'ease-in-out-back',
    'ease-in-elastic',
    'ease-out-elastic',
    'ease-in-out-elastic',
    'ease-in-bounce',
    'ease-out-bounce',
    'ease-in-out-bounce'
];

const OUTLINE_TYPES: readonly NonNullable<TextStyle['outline']>[] = [
    'none',
    'glow',
    'shadow',
    '3d-shadow',
    'stroke',
    '3d-stroke'
];

const BACKGROUND_TYPES: readonly NonNullable<TextStyle['background']>[] = [
    'none',
    'white',
    'colored',
    'translucent'
];

const TEXT_ANIMATION_TYPES: readonly NonNullable<TextStyle['textAnimation']>[] = [
    'none',
    'highlight',
    'boxed',
    'scale',
    'pop',
    'karaoke',
    'reveal',
    'reveal_up',
    'reveal_scale',
    'reveal_box',
    'karaoke_scale',
    'karaoke_box',
    'slide_left',
    'slide_up'
];

function includesString<T extends string>(values: readonly T[], value: unknown): value is T {
    return typeof value === 'string' && values.includes(value as T);
}

function normalizePosition(value: string | undefined): Position | undefined {
    if (!value) {
        return undefined;
    }

    const legacyPositions: Record<string, Position> = {
        'top left': Position.TopLeft,
        'top center': Position.TopCenter,
        'top right': Position.TopRight,
        'middle left': Position.MiddleLeft,
        'middle center': Position.Center,
        'middle right': Position.MiddleRight,
        'center left': Position.MiddleLeft,
        center: Position.Center,
        'center right': Position.MiddleRight,
        'bottom left': Position.BottomLeft,
        'bottom center': Position.BottomCenter,
        'bottom right': Position.BottomRight
    };
    const normalizedLegacyValue = value.trim().toLowerCase().replace(/[-_]+/g, ' ');
    if (legacyPositions[normalizedLegacyValue]) {
        return legacyPositions[normalizedLegacyValue];
    }

    const positions = Object.keys(Position).map(key => Position[key as keyof typeof Position]);
    const trimmedValue = value.trim();
    return positions.includes(trimmedValue as Position) ? trimmedValue as Position : undefined;
}

function normalizeAnimationConfig(state: AnimationState | undefined): AnimationConfig | undefined {
    if (!state || !includesString(ANIMATION_TYPES, state.type)) {
        return undefined;
    }

    const timing: AnimationConfig['timing'] = {
        duration: state.timing.duration,
        delay: state.timing.delay
    };
    if (includesString(EASING_TYPES, state.timing.easing)) {
        timing.easing = state.timing.easing;
    }

    return {
        type: state.type,
        timing,
        timingMode: state.timingMode,
        direction: includesString(ANIMATION_DIRECTIONS, state.direction) ? state.direction : undefined,
        ...(typeof state.scale === 'number' ? { scale: state.scale } : {})
    };
}

function buildCaptionText(tokens: CaptionToken[]): string {
    return tokens.reduce((text, token) => {
        const value = token.text;
        if (!text) {
            return value;
        }
        if (/^[,.;:!?%)]/.test(value) || /[(]$/.test(text)) {
            return `${text}${value}`;
        }
        return `${text} ${value}`;
    }, '');
}

function normalizeTextStyle(state: TextStyleState | undefined): Partial<TextStyle> | undefined {
    if (!state) {
        return undefined;
    }

    return {
        ...(typeof state.primaryColor === 'string' ? { primaryColor: state.primaryColor } : {}),
        ...(typeof state.currentSpokenWordColor === 'string' ? { currentSpokenWordColor: state.currentSpokenWordColor } : {}),
        ...(typeof state.fontFamily === 'string' ? { fontFamily: state.fontFamily } : {}),
        ...(typeof state.maxFontSize === 'number' ? { maxFontSize: state.maxFontSize } : {}),
        ...(includesString(OUTLINE_TYPES, state.outline) ? { outline: state.outline } : {}),
        ...(includesString(BACKGROUND_TYPES, state.background) ? { background: state.background } : {}),
        ...(includesString(TEXT_ANIMATION_TYPES, state.textAnimation) ? { textAnimation: state.textAnimation } : {})
    };
}

function getTimelineBackgroundColor(theme: ThemeSystemConfig): string {
    const legacyTheme = theme as LegacyTimelineTheme;
    return theme.backgroundConfig?.color ||
        (typeof legacyTheme.backgroundColor === 'string' ? legacyTheme.backgroundColor : '#000000');
}

function importMediaState(mediaState: MediaState): MediaObject {
    return TimelineRegistry.import<MediaObject>(mediaState);
}

function isImageMediaObject(mediaObject: MediaObject): mediaObject is Extract<MediaObject, { type: 'image' }> {
    return mediaObject.type === 'image';
}

// Interface for layer grouping in hybrid effect system
interface LayerGroup {
    id: string;
    layers: LayerType[];
    containerBased: boolean;
    effectStyles: React.CSSProperties;
    renderOrder: number; // Maintains layer rendering order
}

// Generic scheduled item for layers other than scenes
export interface ScheduledLayerItem {
    id: string;
    startAt: number;
    duration: number;
    position?: GridPosition; // Optional grid-based position
    size?: GridSpan;         // Optional grid-based size
    content: unknown;        // Could be image, video, text, etc.
}

// Grid positioning based on a virtual responsive grid (e.g., 5x5)
export interface GridPosition {
    row: number;
    col: number;
}

export interface GridSpan {
    rows: number;
    cols: number;
}

export interface Timeline extends Serializable {
    settings: TimelineSettings;
    defaults: TimelineDefaults;
    sceneLayer: SceneLayerContainer;
    backgroundLayer: BackgroundLayerContainer;
    overlayLayer: OverlayLayerContainer;
    overlayTransitionLayer: OverlayTransitionLayerContainer;
    textOverlayLayer: TextOverlayLayerContainer;
    captionLayer: CaptionLayerContainer;
    brandingLayer: BrandingLayerContainer;
    mainAudioLayer: MainAudioLayerContainer; // Main audio track from Trebble editor
    multiTrackEffects: MultiTrackEffect[]; // Keep interface generic for flexibility
    containerEffects: BaseContainerEffect[]; // New container-based effects
    setSettings(settings: Partial<TimelineSettings>): void;
    setDefaults(defaults: TimelineDefaults): void;
    setPremountTime(seconds: number): void;
    addScene(scene: SceneItem): void;
    addBackground(background: Omit<BackgroundItem, 'render'>): void;
    addOverlay(overlay: Omit<OverlayItem, 'render'> | OverlayItemImpl): void;
    addTextOverlay(textOverlay: Omit<TextOverlayItem, 'render' | 'mediaObject'>): void;
    addCaption(caption: CaptionItem | CaptionItemImpl): void;
    addBranding(branding: Omit<BrandingItem, 'render'>): void;
    addMainAudio(audioUrl: string, duration: number, volume?: number): void;
    clearScenes(): void;
    getSceneAtTime(timeInSeconds: number): SceneItem | null;
    exportState(): TimelineState;
    addMultiTrackEffect(effect: MultiTrackEffect): void;
    addContainerEffect(effect: BaseContainerEffect): void;
    addContainerBlur(config: { targetLayers: LayerType[]; blurAmount: number; duration: number; at: number; excludeLayers?: LayerType[] }): void;
    addContainerZoom(config: { targetLayers: LayerType[]; scaleFrom: number; scaleTo: number; duration: number; at: number; excludeLayers?: LayerType[]; transformOrigin?: string }): void;
    calculateFrameRate(): Promise<number>;
    render(): React.ReactNode;
    getAllAnimatedItemsWithSounds(): AnimatedItem[];
}

// Shared utility function for frame rate calculation
export async function calculateTimelineFrameRate(
    sceneLayer: SceneLayerContainer,
    currentFps: number,
    setSettings: (settings: Partial<TimelineSettings>) => void
): Promise<number> {
    try {
        // Get all video sources from scenes
        const videoSources = new Set<string>();

        // Collect video sources from scenes
        sceneLayer.items.forEach((scene: SceneItem) => {
            scene.inputs.forEach((input: MediaObject) => {
                if (input.type === 'video' && 'src' in input) {
                    videoSources.add(input.src);
                }
            });
        });

        // If no videos, return default frame rate
        if (videoSources.size === 0) {
            return currentFps;
        }

        // Analyze each unique video
        const metadataPromises = Array.from(videoSources).map(url =>
            parseMedia({
                src: url,
                fields: {
                    fps: true
                }
            })
        );

        const metadataResults = await Promise.all(metadataPromises);

        // Find the maximum frame rate among all videos
        const frameRates = metadataResults.map(m => m.fps).filter((fps): fps is number => fps !== null);
        if (frameRates.length > 0) {
            const maxFrameRate = Math.max(...frameRates);
            // Update settings with the new frame rate
            setSettings({ fps: maxFrameRate });
            return maxFrameRate;
        }

        // Fallback to default if no valid frame rates found
        return currentFps;
    } catch (error) {
        // Error calculating frame rate
        return currentFps;
    }
}

export class TimelineImpl implements Timeline {
    settings: TimelineSettings;
    defaults: TimelineDefaults;
    designStyle?: DesignStyle;
    videoConfig: { width: number; height: number };
    sceneLayer: SceneLayerContainer;
    backgroundLayer: BackgroundLayerContainer;
    overlayLayer: OverlayLayerContainer;
    overlayTransitionLayer: OverlayTransitionLayerContainer;
    textOverlayLayer: TextOverlayLayerContainer;
    captionLayer: CaptionLayerContainer;
    brandingLayer: BrandingLayerContainer;
    mainAudioLayer: MainAudioLayerContainer;
    multiTrackEffects: MultiTrackEffect[];
    containerEffects: BaseContainerEffect[]; // New container-based effects
    durationInSeconds?: number; // Automatic timeline duration calculation
    // Used by SequencerToTimeloomConverter to align video timeline with project timeline in ms
    // Enables accurate video seeking relative to the entire sequencer project
    absoluteStartTimeMs?: number;

    constructor(config: {
        fps: number;
        theme: ThemeSystemConfig;
        defaults: TimelineDefaults;
        aspectRatio: AspectRatio;
        resolutionTier?: ResolutionTier;
        designStyle?: DesignStyle;
        videoConfig?: { width: number; height: number };
    }) {
        this.settings = {
            fps: config.fps,
            theme: config.theme,
            aspectRatio: config.aspectRatio,
            resolutionTier: config.resolutionTier,
            // Premounting disabled - didn't fix black screen issue
            premountFrames: 0,
            disableVideoSync: false
        };
        this.defaults = config.defaults;
        this.designStyle = config.designStyle;

        // Calculate video dimensions from aspect ratio and resolution tier if not explicitly provided
        // Priority: explicit videoConfig > aspectRatio + resolutionTier > aspectRatio alone (defaults to 1080p)
        this.videoConfig = config.videoConfig || getVideoConfigFromAspectRatio(config.aspectRatio, config.resolutionTier);

        if (process.env.NODE_ENV === 'development') {
            console.log('[Timeline constructor] Configuration:', {
                providedAspectRatio: config.aspectRatio,
                providedResolutionTier: config.resolutionTier,
                providedVideoConfig: config.videoConfig,
                finalVideoConfig: this.videoConfig,
                settingsAspectRatio: this.settings.aspectRatio,
                settingsResolutionTier: this.settings.resolutionTier
            });
        }
        
        // Initialize all layers
        this.sceneLayer = new SceneLayerContainer([]);
        this.backgroundLayer = new BackgroundLayerContainer([]);
        this.overlayLayer = new OverlayLayerContainer([]);
        this.overlayTransitionLayer = new OverlayTransitionLayerContainer();
        this.textOverlayLayer = new TextOverlayLayerContainer([]);
        this.captionLayer = new CaptionLayerContainer([]);
        this.brandingLayer = new BrandingLayerContainer([]);
        this.mainAudioLayer = new MainAudioLayerContainer();
        this.multiTrackEffects = [];
        this.containerEffects = [];
    }

    setSettings(settings: Partial<TimelineSettings>): void {
        this.settings = { ...this.settings, ...settings };
    }

    setDefaults(defaults: TimelineDefaults): void {
        this.defaults = defaults;
    }

    setPremountTime(seconds: number): void {
        const frames = Math.round(seconds * this.settings.fps);
        this.settings.premountFrames = frames;
    }

    addScene(scene: SceneItem): void {
        this.sceneLayer.addScene(scene);
    }

    clearScenes(): void {
        // Clear in-place to preserve external references
        this.sceneLayer.items.length = 0;
    }

    getSceneAtTime(timeInSeconds: number): SceneItem | null {
        for (const scene of this.sceneLayer.items) {
            const sceneStart = scene.startAt;
            // Undefined duration means scene extends infinitely (matches any time >= start)
            const sceneEnd = scene.duration !== undefined ? sceneStart + scene.duration : Infinity;
            if (timeInSeconds >= sceneStart && timeInSeconds < sceneEnd) {
                return scene;
            }
        }
        return null;
    }

    addBackground(background: Omit<BackgroundItem, 'render'>): void {
        this.backgroundLayer.items.push(new BackgroundItemImpl(background));
    }

    addOverlay(overlay: Omit<OverlayItem, 'render'> | OverlayItemImpl): void {
        // Type safety check: Prevent TextOverlayItemImpl from being added to overlay layer
        if (overlay instanceof TextOverlayItemImpl) {
            throw new Error(
                `TextOverlayItemImpl cannot be added to overlay layer. ` +
                `Use timeline.addTextOverlay() instead of timeline.addOverlay() for text overlays. ` +
                `Item ID: ${overlay.id}`
            );
        }
        
        // If it's already an implementation, use it directly to preserve animations
        if (overlay instanceof OverlayItemImpl) {
            this.overlayLayer.items.push(overlay);
        } else {
            // If it's just an interface, create a new implementation
            this.overlayLayer.items.push(new OverlayItemImpl(overlay));
        }
    }

    addTextOverlay(textOverlay: Omit<TextOverlayItem, 'render' | 'mediaObject'>): void {
        this.textOverlayLayer.items.push(new TextOverlayItemImpl(textOverlay));
    }

    addCaption(caption: CaptionItem | CaptionItemImpl): void {
        // If it's already an implementation, use it directly to preserve advanced styling
        if (caption instanceof CaptionItemImpl) {
            this.captionLayer.items.push(caption);
        } else {
            // If it's just an interface, create a new implementation
            this.captionLayer.items.push(new CaptionItemImpl(caption));
        }
    }

    addBranding(branding: Omit<BrandingItem, 'render'>): void {
        this.brandingLayer.items.push(new BrandingItemImpl(branding));
    }

    addMainAudio(audioUrl: string, duration: number, volume: number = 1.0): void {
        this.mainAudioLayer.setAudio(audioUrl, duration, volume);

        if (process.env.NODE_ENV === 'development') {
            console.log('[Timeline] Main audio added:', {
                audioUrl,
                duration,
                volume,
                hasAudio: !!this.mainAudioLayer.audioUrl
            });
        }
    }

    addMultiTrackEffect(effect: MultiTrackEffect): void {
        this.multiTrackEffects.push(effect);
    }

    addContainerEffect(effect: BaseContainerEffect): void {
        this.containerEffects.push(effect);
    }

    addContainerBlur(config: {
        targetLayers: LayerType[];
        blurAmount: number;
        duration: number;
        at: number;
        excludeLayers?: LayerType[];
    }): void {
        const effect = ContainerBlurEffect.createCustom({
            targetLayers: config.targetLayers,
            excludeLayers: config.excludeLayers,
            blurFrom: 0,
            blurTo: config.blurAmount,
            duration: config.duration,
            at: config.at
        });
        this.addContainerEffect(effect);
    }

    addContainerZoom(config: {
        targetLayers: LayerType[];
        scaleFrom: number;
        scaleTo: number;
        duration: number;
        at: number;
        excludeLayers?: LayerType[];
        transformOrigin?: string;
    }): void {
        const effect = ContainerZoomEffect.createCustom({
            targetLayers: config.targetLayers,
            excludeLayers: config.excludeLayers,
            scaleFrom: config.scaleFrom,
            scaleTo: config.scaleTo,
            duration: config.duration,
            at: config.at,
            transformOrigin: config.transformOrigin
        });
        this.addContainerEffect(effect);
    }

    // User-friendly helper methods for common multi-track zoom scenarios

    /**
     * Apply zoom effect to all layers except captions and branding
     * Most common use case for content zooming while keeping UI elements fixed
     */
    addZoomExcludingCaptions(
        from: number,
        to: number,
        duration: number,
        at: number,
        isPercent: boolean = false
    ): void {
        const effect = new ContainerZoomEffect({
            targetLayers: ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay'],
            excludeLayers: ['caption', 'branding'],
            scaleFrom: isPercent ? from / 100 : from,
            scaleTo: isPercent ? to / 100 : to,
            duration,
            at,
            transformOrigin: 'center center'
        });
        this.addContainerEffect(effect);
    }

    /**
     * Apply zoom effect only to main content (background, scene, overlay)
     * Excludes text overlays, captions, and branding
     */
    addZoomMainContentOnly(
        from: number,
        to: number,
        duration: number,
        at: number,
        isPercent: boolean = false
    ): void {
        const effect = new ContainerZoomEffect({
            targetLayers: ['background', 'scene'],
            excludeLayers: ['overlay', 'overlayTransition', 'textOverlay', 'caption', 'branding'],
            scaleFrom: isPercent ? from / 100 : from,
            scaleTo: isPercent ? to / 100 : to,
            duration,
            at,
            transformOrigin: 'center center'
        });
        this.addContainerEffect(effect);
    }

    /**
     * Apply zoom effect to all layers except branding
     * Useful when you want captions to zoom with content but keep logo fixed
     */
    addZoomExceptBranding(
        from: number,
        to: number,
        duration: number,
        at: number,
        isPercent: boolean = false
    ): void {
        const effect = MultiTrackZoomEffect.createAllExceptBranding(
            from, to, duration, at,
            undefined // use default easing
        );
        if (isPercent) {
            effect.from = from / 100;
            effect.to = to / 100;
        }
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply zoom effect to custom layer combination
     * For advanced users who want full control over which layers are affected
     */
    addZoomToLayers(
        layers: LayerType[],
        from: number,
        to: number,
        duration: number,
        at: number,
        excludeLayers?: LayerType[],
        isPercent: boolean = false
    ): void {
        const effect = ContainerZoomEffect.createCustom({
            targetLayers: layers,
            excludeLayers: excludeLayers,
            scaleFrom: from,
            scaleTo: to,
            duration: duration,
            at: at,
            transformOrigin: 'center center'
        });
        this.addContainerEffect(effect);
    }

    // User-friendly helper methods for common multi-track blur scenarios

    /**
     * Apply blur effect to all layers except captions and branding
     * Perfect for scene transitions where you want to blur content while keeping text readable
     */
    addBlurExcludingCaptions(
        from: number,
        to: number,
        duration: number,
        at: number
    ): void {
        
        const effect = PerLayerBlurEffect.createExcludingCaptions(
            from, to, duration, at,
            undefined // use default easing
        );
        
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply blur effect only to main content (background, scene, overlay)
     * Excludes text overlays, captions, and branding
     */
    addBlurMainContentOnly(
        from: number,
        to: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerBlurEffect.createMainContentOnly(
            from, to, duration, at,
            undefined // use default easing
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply blur effect to all layers except branding
     * Useful when you want captions to blur with content but keep logo sharp
     */
    addBlurExceptBranding(
        from: number,
        to: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerBlurEffect.createAllExceptBranding(
            from, to, duration, at,
            undefined // use default easing
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Create a scene transition blur effect
     * Quick blur in/out with moderate intensity, perfect for transitioning between scenes
     */
    addSceneTransitionBlur(
        duration: number,
        at: number,
        maxBlur: number = 10
    ): void {
        const effect = PerLayerBlurEffect.createSceneTransition(
            duration, at, maxBlur,
            undefined // use default easing
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply blur effect to custom layer combination
     * For advanced users who want full control over which layers are affected
     */
    addBlurToLayers(
        layers: LayerType[],
        from: number,
        to: number,
        duration: number,
        at: number,
        excludeLayers?: LayerType[]
    ): void {
        const effect = new PerLayerBlurEffect({
            from, to, duration, at,
            targetLayers: layers,
            excludeLayers,
            easing: undefined // use default easing
        });
        this.addMultiTrackEffect(effect);
    }

    // User-friendly helper methods for static (non-animated) blur effects

    /**
     * Apply static blur to specific layers - MOST FLEXIBLE
     * Pick and choose exactly which layers to blur
     * 
     * Available layers: 'background', 'scene', 'overlay', 'overlayTransition', 'textOverlay', 'caption', 'branding'
     * 
     * Examples:
     * - Background only: addStaticBlur(['background'], 25, 2, 5)
     * - Video content only: addStaticBlur(['background', 'scene'], 25, 2, 5)  
     * - Text only: addStaticBlur(['textOverlay', 'caption'], 15, 2, 8)
     * - Everything: addStaticBlur(['background', 'scene', 'overlay', 'textOverlay', 'caption', 'branding'], 30, 2, 10)
     */
    addStaticBlur(
        layers: LayerType[],
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        
        const effect = PerLayerStaticBlurEffect.forLayers(
            layers, blurAmount, duration, at
        );
        
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply static blur to background and scene only
     * Perfect for blurring video content while keeping all text/UI sharp
     */
    addStaticBlurBackgroundAndScene(
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerStaticBlurEffect.forBackgroundAndScene(
            blurAmount, duration, at
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply static blur to background only
     * Only blurs background images/videos, keeps everything else sharp
     */
    addStaticBlurBackgroundOnly(
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerStaticBlurEffect.forBackgroundOnly(
            blurAmount, duration, at
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply static blur to scene (main video) only
     * Only blurs the main video content, keeps everything else sharp
     */
    addStaticBlurSceneOnly(
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerStaticBlurEffect.forSceneOnly(
            blurAmount, duration, at
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply static blur to text overlays only
     * Only blurs text content while keeping video sharp
     */
    addStaticBlurTextOnly(
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerStaticBlurEffect.forTextOnly(
            blurAmount, duration, at
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply static blur to all layers
     * Blurs everything including text and branding
     */
    addStaticBlurAll(
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerStaticBlurEffect.forAllLayers(
            blurAmount, duration, at
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply static blur to all layers except captions and branding
     * Perfect for content blur while keeping text readable
     */
    addStaticBlurExcludingCaptions(
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerStaticBlurEffect.forLayers(
            ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay'],
            blurAmount, duration, at
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply static blur to main content only (background, scene, overlay)
     * Excludes text overlays, captions, and branding
     */
    addStaticBlurMainContentOnly(
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerStaticBlurEffect.forLayers(
            ['background', 'scene', 'overlay'],
            blurAmount, duration, at
        );
        this.addMultiTrackEffect(effect);
    }

    /**
     * Apply static blur to all layers except branding
     * Useful when you want captions to blur with content but keep logo sharp
     */
    addStaticBlurExceptBranding(
        blurAmount: number,
        duration: number,
        at: number
    ): void {
        const effect = PerLayerStaticBlurEffect.forLayers(
            ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay', 'caption'],
            blurAmount, duration, at
        );
        this.addMultiTrackEffect(effect);
    }

    getAllAnimatedItemsWithSounds(): AnimatedItem[] {
        const items: AnimatedItem[] = [];

        // Helper function to check if item has any sound effects
        const hasAnySound = (item: AnimatedItem) => 
            item.entranceSoundEffect || 
            item.exitSoundEffect || 
            (item.customSoundEffects && item.customSoundEffects.length > 0);

        // Collect from all layers (including scenes and overlay transitions!)
        items.push(...this.sceneLayer.items.filter(hasAnySound));
        items.push(...this.overlayLayer.items.filter(hasAnySound));
        items.push(...this.overlayTransitionLayer.items.filter(hasAnySound));
        items.push(...this.textOverlayLayer.items.filter(hasAnySound));
        items.push(...this.captionLayer.items.filter(hasAnySound));
        items.push(...this.brandingLayer.items.filter(hasAnySound));

        return items;
    }

    /**
     * Render sound effects for all animated items in the timeline
     */
    public renderSoundEffects(): React.ReactNode[] {
        const soundElements: React.ReactNode[] = [];
        const animatedItems = this.getAllAnimatedItemsWithSounds();

        animatedItems.forEach(item => {
            // Render entrance sound effect using centralized timing
            if (item.entranceSoundEffect && item.entranceAnimation) {
                try {
                    const soundDelay = item.entranceSoundEffect.timing?.delay ?? 0;
                    const originalDuration = item.entranceSoundEffect.metadata?.originalDuration ?? 0;
                    
                    const constraints = SequenceTimingHelper.calculateSoundConstraints(
                        item.startAt,
                        item.duration,
                        'entrance',
                        item.entranceAnimation,
                        soundDelay,
                        originalDuration
                    );
                    
                    if (constraints.isValid) {
                        // Sound timing calculated for entrance animation
                        
                        soundElements.push(
                            <SoundPlayer
                                key={`entrance-sound-${item.id}`}
                                config={item.entranceSoundEffect}
                                constraints={constraints}
                                itemId={item.id}
                                soundType="entrance"
                                onError={(error) => { /* Entrance sound error */ }}
                            />
                        );
                    } else {
                        // Invalid entrance sound constraints
                    }
                } catch (error) {
                    // Failed to render entrance sound
                }
            }

            // Render exit sound effect using centralized timing
            if (item.exitSoundEffect && item.exitAnimation) {
                try {
                    const soundDelay = item.exitSoundEffect.timing?.delay ?? 0;
                    const originalDuration = item.exitSoundEffect.metadata?.originalDuration ?? 0;
                    
                    const constraints = SequenceTimingHelper.calculateSoundConstraints(
                        item.startAt,
                        item.duration,
                        'exit',
                        item.exitAnimation,
                        soundDelay,
                        originalDuration
                    );
                    
                    if (constraints.isValid) {
                        // Sound timing calculated for exit animation
                        
                        soundElements.push(
                            <SoundPlayer
                                key={`exit-sound-${item.id}`}
                                config={item.exitSoundEffect}
                                constraints={constraints}
                                itemId={item.id}
                                soundType="exit"
                                onError={(error) => { /* Exit sound error */ }}
                            />
                        );
                    } else {
                        // Invalid exit sound constraints
                    }
                } catch (error) {
                    // Failed to render exit sound
                }
            }

            // Render custom sound effects
            if (item.customSoundEffects && item.customSoundEffects.length > 0) {
                item.customSoundEffects.forEach((customSound, index) => {
                    try {
                        // For custom sounds, we need to create a simpler constraint system
                        // since they don't follow the entrance/exit pattern
                        const delay = customSound.timing?.delay ?? 0;
                        const itemStartTime = item.startAt;
                        const itemEndTime = item.startAt + (item.duration ?? 0);
                        const soundStartTime = itemStartTime + delay;
                        const maxDuration = Math.max(0, itemEndTime - soundStartTime);
                        
                        const customConstraints = {
                            windowStart: soundStartTime,
                            windowEnd: itemEndTime,
                            maxDuration,
                            effectiveStart: soundStartTime,
                            effectiveEnd: soundStartTime + maxDuration,
                            willBeTrimmed: (customSound.metadata?.originalDuration ?? 0) > maxDuration,
                            isValid: maxDuration > 0 && soundStartTime < itemEndTime
                        };

                        if (customConstraints.isValid) {
                            soundElements.push(
                                <SoundPlayer
                                    key={`custom-sound-${item.id}-${index}`}
                                    config={customSound}
                                    constraints={customConstraints}
                                    itemId={item.id}
                                    soundType="custom"
                                    onError={(error) => { /* Custom sound error */ }}
                                />
                            );
                        } else {
                            // Invalid custom sound constraints
                        }
                    } catch (error) {
                        // Failed to render custom sound
                    }
                });
            }
        });

        return soundElements;
    }

    async calculateFrameRate(): Promise<number> {
        return calculateTimelineFrameRate(
            this.sceneLayer,
            this.settings.fps,
            this.setSettings.bind(this)
        );
    }

    /**
     * Process overlay transitions from scenes automatically
     * This method should be called after all scenes are added to create the overlay transitions
     * (Same logic as BasicTimeline.processOverlayTransitions)
     */
    processOverlayTransitions(): void {
        const scenes = this.sceneLayer.items;
        
        scenes.forEach((scene, index) => {
            if (scene.endOverlayTransition) {
                const overlayConfig = scene.endOverlayTransition;
                const overlayDuration = overlayConfig.duration;
                
                // Calculate overlay start time: half before scene end, half after
                const sceneEndTime = scene.startAt + (scene.duration || 0);
                const overlayStartTime = sceneEndTime - (overlayDuration / 2);
                
                // Create overlay transition item
                const overlayTransition = new OverlayTransitionItemImpl({
                    id: `overlay-${scene.id}`,
                    startAt: overlayStartTime,
                    duration: overlayDuration,
                    overlayDuration: overlayDuration,
                    videoSrc: overlayConfig.videoSrc,
                    associatedSceneId: scene.id,
                    audioEffect: overlayConfig.audioEffect,
                    transparencyMode: overlayConfig.transparencyMode
                });
                
                // Add to overlay transition layer
                this.overlayTransitionLayer.addOverlayTransition(overlayTransition);
                
                if (process.env.NODE_ENV === 'development') {
                    // Overlay transition processed
                }
            }
        });
        
        if (process.env.NODE_ENV === 'development') {
            const transitionCount = this.overlayTransitionLayer.items.length;
            // Overlay transitions processed
        }
    }

    render(): React.ReactNode {
        // Automatically calculate required timeline duration including animation buffers
        const durationResult = ensureTimelineDuration(this);
        
        return (
            <TimelineRenderer timeline={this} />
        );
    }

    /**
     * Get the calculated timeline duration including animation buffers
     */
    getDuration(): number {
        return this.durationInSeconds || 0;
    }

    /**
     * Generate a detailed duration analysis report
     */
    generateDurationReport(): string {
        return TimelineDurationCalculator.generateDurationReport(this);
    }

    /**
     * Validate timeline duration and get recommendations
     */
    validateDuration(): {
        isValid: boolean;
        issues: string[];
        recommendations: string[];
    } {
        return TimelineDurationCalculator.validateTimelineDuration(this);
    }

    /**
     * Manually update timeline duration if needed
     */
    updateDuration(): boolean {
        return TimelineDurationCalculator.updateTimelineDuration(this);
    }

    /**
     * Export the Timeline state as JSON-serializable data
     * This enables identical rendering between browser and cloud
     */
    exportState(): TimelineState {
        // Calculate total duration from scenes
        let maxEndTime = 0;
        this.sceneLayer.items.forEach(scene => {
            const sceneEnd = scene.startAt + (scene.duration || 0);
            if (sceneEnd > maxEndTime) {
                maxEndTime = sceneEnd;
            }
        });
        const totalDuration = maxEndTime || this.getDuration() || 0;

        // Minimum duration for fallback (1 frame in seconds)
        const minFrameSec = 1 / this.settings.fps;

        const state: TimelineState = {
            type: 'Timeline',
            version: '1.0.0',
            fps: this.settings.fps,
            aspectRatio: this.settings.aspectRatio,
            settings: {
                width: this.videoConfig.width,
                height: this.videoConfig.height,
                durationInFrames: Math.ceil(totalDuration * this.settings.fps),
                backgroundColor: getTimelineBackgroundColor(this.settings.theme)
            },
            defaults: this.defaults ? {
                layoutPadding: this.defaults.layoutPadding,
                transitionIn: this.defaults.transitionIn,
                transitionOut: this.defaults.transitionOut,
                premountFrames: this.defaults.premountFrames
            } : undefined,
            scenes: this.sceneLayer.items.map(scene => {
                // Scenes need to implement exportState
                if (hasExportState<SceneState>(scene)) {
                    return scene.exportState();
                }
                // Fallback for scenes without exportState (temporary)
                return {
                    type: 'Scene',
                    version: '1.0.0',
                    id: scene.id,
                    startAt: scene.startAt,
                    duration: Math.max(scene.duration || 0, minFrameSec),
                    layout: scene.layout || 'default',
                    objectFit: scene.objectFit,
                    inputs: []
                };
            }),
            overlays: this.overlayLayer.items.map(overlay => {
                if (hasExportState<OverlayState>(overlay)) {
                    return overlay.exportState();
                }
                return {
                    type: 'Overlay',
                    version: '1.0.0',
                    id: overlay.id,
                    startAt: overlay.startAt,
                    duration: Math.max(overlay.duration || 0, minFrameSec),
                    mediaObject: { type: 'VideoMedia', version: '1.0.0', id: 'temp', src: '' }
                };
            }),
            textOverlays: this.textOverlayLayer.items.map(textOverlay => {
                if (hasExportState<TextOverlayState>(textOverlay)) {
                    return textOverlay.exportState();
                }
                return {
                    type: 'TextOverlay',
                    version: '1.0.0',
                    id: textOverlay.id,
                    startAt: textOverlay.startAt,
                    duration: Math.max(textOverlay.duration || 0, minFrameSec),
                    text: textOverlay.text || ''
                };
            }),
            captions: this.captionLayer.items.map(caption => {
                if (hasExportState<CaptionState>(caption)) {
                    return caption.exportState();
                }
                return {
                    type: 'Caption',
                    version: '1.0.0',
                    id: caption.id,
                    startAt: caption.startAt,
                    duration: Math.max(caption.duration || 0, minFrameSec),
                    tokens: []
                };
            }),
            branding: this.brandingLayer.items.map(branding => {
                if (hasExportState<BrandingState>(branding)) {
                    return branding.exportState();
                }
                return {
                    type: 'Branding',
                    version: '1.0.0',
                    id: branding.id,
                    startAt: branding.startAt,
                    duration: Math.max(branding.duration || 0, minFrameSec),
                    mediaObject: { type: 'VideoMedia', version: '1.0.0', id: 'temp', src: '' }
                };
            }),
            mainAudio: this.mainAudioLayer.audioUrl ? {
                audioUrl: this.mainAudioLayer.audioUrl,
                duration: this.mainAudioLayer.duration,
                volume: this.mainAudioLayer.volume
            } : undefined
        };

        // Validate the exported state
        try {
            validateTimelineState(state);
        } catch (error) {
            console.error('[Timeline] Export validation failed:', error);
            throw error; // Re-throw to prevent returning invalid state
        }

        return state;
    }

    private static importOverlayState(state: OverlayState): OverlayItemImpl {
        return new OverlayItemImpl({
            id: state.id,
            startAt: state.startAt,
            duration: state.duration,
            mediaObject: importMediaState(state.mediaObject),
            position: normalizePosition(state.position),
            gridArea: state.gridArea,
            entranceAnimation: normalizeAnimationConfig(state.entranceAnimation),
            exitAnimation: normalizeAnimationConfig(state.exitAnimation)
        });
    }

    private static importTextOverlayState(state: TextOverlayState): TextOverlayItemImpl {
        return new TextOverlayItemImpl({
            id: state.id,
            startAt: state.startAt,
            duration: state.duration,
            text: state.text,
            position: normalizePosition(state.position),
            gridArea: state.gridArea,
            textStyle: normalizeTextStyle(state.textStyle),
            entranceAnimation: normalizeAnimationConfig(state.entranceAnimation),
            exitAnimation: normalizeAnimationConfig(state.exitAnimation)
        });
    }

    private static importCaptionState(state: CaptionState): CaptionItemImpl {
        const tokens: CaptionToken[] = state.tokens.map((token, index) => ({
            text: token.text,
            fromMs: token.fromMs ?? 0,
            toMs: token.toMs ?? token.fromMs ?? 0,
            index
        }));

        return new CaptionItemImpl({
            id: state.id,
            startAt: state.startAt,
            duration: state.duration,
            text: buildCaptionText(tokens),
            tokens,
            position: normalizePosition(state.position),
            textStyle: normalizeTextStyle(state.textStyle)
        });
    }

    private static importBrandingState(state: BrandingState): BrandingItemImpl | null {
        const mediaObject = importMediaState(state.mediaObject);
        if (!isImageMediaObject(mediaObject)) {
            console.warn('[Timeline] Branding mediaObject must be image media, skipping:', state.id);
            return null;
        }

        return new BrandingItemImpl({
            id: state.id,
            startAt: state.startAt,
            duration: state.duration,
            mediaObject,
            position: normalizePosition(state.position),
            gridArea: state.gridArea
        });
    }

    /**
     * Import Timeline from serialized state
     * This reconstructs an identical Timeline from JSON data
     */
    static importState(state: TimelineState): TimelineImpl {
        // Validate the state before importing
        const validatedState = validateTimelineState(state);

        // Create new Timeline with settings from state
        const timeline = new TimelineImpl({
            fps: validatedState.fps,
            aspectRatio: validatedState.aspectRatio as AspectRatio,
            theme: createTimelineTheme(validatedState.settings.backgroundColor),
            defaults: validatedState.defaults ? {
                layoutPadding: validatedState.defaults.layoutPadding ?? 0,
                transitionIn: validatedState.defaults.transitionIn as TransitionConfig ?? { type: 'none', duration: 0, mode: 'prefix' },
                transitionOut: validatedState.defaults.transitionOut as TransitionConfig ?? { type: 'none', duration: 0, mode: 'postfix' },
                premountFrames: validatedState.defaults.premountFrames ?? 0
            } : {
                layoutPadding: 0,
                transitionIn: { type: 'none', duration: 0, mode: 'prefix' },
                transitionOut: { type: 'none', duration: 0, mode: 'postfix' },
                premountFrames: 0
            },
            videoConfig: {
                width: validatedState.settings.width,
                height: validatedState.settings.height
            }
        });

        // Import scenes
        validatedState.scenes.forEach(sceneState => {
            // Try to use Scene's importState if available
            if (TimelineRegistry.hasType('Scene')) {
                const scene = TimelineRegistry.import<SceneItem>(sceneState);
                timeline.addScene(scene);
            } else {
                // Fallback: reconstruct manually (temporary)
                console.warn('[Timeline] Scene importState not registered, using fallback');
                // This would be replaced with proper Scene.importState
            }
        });

        // Import overlays
        validatedState.overlays.forEach(overlayState => {
            timeline.overlayLayer.items.push(TimelineImpl.importOverlayState(overlayState));
        });

        // Import text overlays
        validatedState.textOverlays.forEach(textOverlayState => {
            timeline.textOverlayLayer.items.push(TimelineImpl.importTextOverlayState(textOverlayState));
        });

        // Import captions
        validatedState.captions.forEach(captionState => {
            timeline.captionLayer.items.push(TimelineImpl.importCaptionState(captionState));
        });

        // Import branding
        validatedState.branding.forEach(brandingState => {
            const branding = TimelineImpl.importBrandingState(brandingState);
            if (branding) {
                timeline.brandingLayer.items.push(branding);
            }
        });

        // Import main audio if present
        if (validatedState.mainAudio) {
            timeline.addMainAudio(
                validatedState.mainAudio.audioUrl,
                validatedState.mainAudio.duration,
                validatedState.mainAudio.volume
            );
        }

        return timeline;
    }
}

/**
 * Analyzes active multi-track effects and creates layer groups for container-based vs per-layer rendering
 * 
 * This function implements intelligent grouping with predefined layer groups for common use cases:
 * 1. "Background + Scene" group - Video content that should zoom/move together
 * 2. "Content without UI" group - Everything except branding and captions
 * 3. Individual layers for remaining cases
 * 4. Maintaining proper layer rendering order
 */
function analyzeEffectsAndCreateGroups(
    effects: MultiTrackEffect[],
    containerEffects: BaseContainerEffect[], 
    currentTime: number
): LayerGroup[] {
    // Standard layer rendering order (must be preserved)
    const layerOrder: LayerType[] = ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay', 'caption', 'branding'];
    
    // Predefined logical layer groups for common use cases
    const PREDEFINED_GROUPS = {
        // Video content that should move together (backgrounds + main scenes)
        VIDEO_CONTENT: ['background', 'scene'] as LayerType[],
        
        // All content except UI elements (everything but captions and branding)
        CONTENT_WITHOUT_UI: ['background', 'scene', 'overlay', 'overlayTransition', 'textOverlay'] as LayerType[],
        
        // UI elements that usually stay fixed
        UI_ELEMENTS: ['caption', 'branding'] as LayerType[]
    };
    
    // Collect active effects at current time (combine multi-track and container effects)
    const activeMultiTrackEffects = effects.filter(effect => {
        const progress = effect.calculateProgress(currentTime);
        return progress > 0 && progress <= 1;
    });

    const activeContainerEffects = containerEffects.filter(effect => {
        const progress = effect.calculateProgress(currentTime);
        return progress > 0 && progress <= 1;
    });

    // Combine all active effects for analysis
    const allActiveEffects = [...activeMultiTrackEffects, ...activeContainerEffects];

    // Separate container-based from per-layer effects
    // MultiTrackEffect has containerBased property, BaseContainerEffect is always container-based
    const containerBasedEffects = allActiveEffects.filter(effect => 
        'containerBased' in effect ? effect.containerBased : true
    );
    const perLayerEffects = activeMultiTrackEffects.filter(effect => !effect.containerBased);

    // Calculate styles for each layer
    const layerStyles: Record<LayerType, React.CSSProperties> = {
        background: {},
        scene: {},
        overlay: {},
        overlayTransition: {},
        textOverlay: {},
        caption: {},
        branding: {}
    };

    // Apply per-layer effects (these don't affect grouping)
    perLayerEffects.forEach(effect => {
        const progress = effect.calculateProgress(currentTime);
        const effectStyles = effect.getContainerStyle(progress);
        
        effect.targetLayers.forEach(layerType => {
            if (effect.shouldApplyToLayer(layerType)) {
                layerStyles[layerType] = {
                    ...layerStyles[layerType],
                    ...effectStyles
                };
            }
        });
    });

    // Analyze container-based effects for intelligent grouping
    const containerGroups: LayerGroup[] = [];
    const processedLayers = new Set<LayerType>();

    // Group layers using intelligent predefined groups
    containerBasedEffects.forEach((effect, effectIndex) => {
        // Handle both MultiTrackEffect and BaseContainerEffect interfaces
        let progress: number;
        let effectStyles: React.CSSProperties;
        let affectedLayers: LayerType[];

        if ('containerBased' in effect) {
            // MultiTrackEffect
            progress = effect.calculateProgress(currentTime);
            effectStyles = effect.getContainerStyle(progress);
            affectedLayers = effect.targetLayers.filter(layer => 
                effect.shouldApplyToLayer(layer) && !processedLayers.has(layer)
            );
        } else {
            // BaseContainerEffect
            progress = effect.calculateProgress(currentTime);
            effectStyles = effect.getContainerStyle(progress);
            affectedLayers = effect.getAffectedLayers().filter(layer => 
                !processedLayers.has(layer)
            );
        }

        if (affectedLayers.length > 0) {
            // Simply use all affected layers - don't try to be clever with predefined groups
            // The container effect already specified exactly which layers it wants
            let groupLayers: LayerType[] = affectedLayers;
            let groupName = 'container-effect';

            // Determine a descriptive name based on what's included
            if (affectedLayers.length === 2 && 
                affectedLayers.includes('background') && affectedLayers.includes('scene')) {
                groupName = 'video-content';
            } else if (affectedLayers.includes('background') && 
                       affectedLayers.includes('scene') && 
                       affectedLayers.includes('overlay')) {
                groupName = 'main-content';
            } else if (affectedLayers.length >= 4) {
                groupName = 'all-content';
            } else {
                groupName = 'custom';
            }

            // Create container group
            containerGroups.push({
                id: `container-${groupName}-${effectIndex}`,
                layers: groupLayers,
                containerBased: true,
                effectStyles,
                renderOrder: Math.min(...groupLayers.map(layer => layerOrder.indexOf(layer)))
            });

            // Mark these layers as processed
            groupLayers.forEach(layer => processedLayers.add(layer));
        }
    });

    // Add remaining layers as individual per-layer groups
    layerOrder.forEach((layer, index) => {
        if (!processedLayers.has(layer)) {
            containerGroups.push({
                id: `layer-${layer}`,
                layers: [layer],
                containerBased: false,
                effectStyles: layerStyles[layer],
                renderOrder: index
            });
        }
    });

    // Sort groups by render order to maintain layer stacking
    containerGroups.sort((a, b) => a.renderOrder - b.renderOrder);


    return containerGroups;
}

/**
 * TimelineRenderer component handles the rendering of all layers with multi-track effects applied
 */
const TimelineRenderer: React.FC<{ timeline: TimelineImpl }> = ({ timeline }) => {
    const currentFrame = useCurrentFrame();
    const remotionVideoConfig = useVideoConfig();
    const { fps, theme, aspectRatio } = timeline.settings;
    // Use Remotion's fps for timing calculation (currentFrame is based on Remotion's composition fps)
    const currentTime = currentFrame / remotionVideoConfig.fps;

    // Use timeline's configured dimensions instead of Remotion runtime config
    // This ensures consistent rendering between browser and Lambda
    const videoConfig = timeline.videoConfig;

    // Merge settings into defaults for scene rendering
    const defaults: TimelineDefaults = {
        ...timeline.defaults,
        premountFrames: timeline.settings.premountFrames ?? 0
    };

    // NEW HYBRID SYSTEM: Analyze effects and create layer groups
    const layerGroups = analyzeEffectsAndCreateGroups(timeline.multiTrackEffects, timeline.containerEffects, currentTime);

    // Helper to render individual layer content
    const renderLayerContent = (layerType: LayerType): React.ReactNode => {
        switch (layerType) {
            case 'background':
                return timeline.backgroundLayer.render(fps, theme, aspectRatio, defaults, videoConfig);
            case 'scene':
                return timeline.sceneLayer.render(fps, theme, aspectRatio, defaults, videoConfig);
            case 'overlay':
                return timeline.overlayLayer.render(fps, theme, aspectRatio, defaults, videoConfig);
            case 'overlayTransition':
                return timeline.overlayTransitionLayer.render(fps, theme, aspectRatio, defaults, videoConfig);
            case 'textOverlay':
                return timeline.textOverlayLayer.render(fps, theme, aspectRatio, defaults, videoConfig);
            case 'caption':
                return timeline.captionLayer.render(fps, theme, aspectRatio, defaults, videoConfig);
            case 'branding':
                return timeline.brandingLayer.render(fps, theme, aspectRatio, defaults, videoConfig);
            default:
                return null;
        }
    };

    // Helper to get CSS class name for a layer
    const getLayerClassName = (layer: LayerType): string => {
        switch (layer) {
            case 'background': return 'timeloom-background-layer';
            case 'scene': return 'timeloom-scene-layer';
            case 'overlay': return 'timeloom-overlay-layer';
            case 'overlayTransition': return 'timeloom-overlay-transition-layer';
            case 'textOverlay': return 'timeloom-text-overlay-layer';
            case 'caption': return 'timeloom-caption-layer';
            case 'branding': return 'timeloom-branding-layer';
            default: return 'timeloom-unknown-layer';
        }
    };

    // Helper to render a layer group (container-based or per-layer)
    const renderLayerGroup = (group: LayerGroup): React.ReactNode => {
        const hasEffects = Object.keys(group.effectStyles).length > 0;

        if (group.containerBased && hasEffects) {
            // CONTAINER-BASED: Wrap all layers in a single container with effects
            
            const containerStyles = {
                position: 'absolute' as const,
                width: '100%',
                height: '100%',
                ...group.effectStyles
            };

            return (
                <div key={group.id} style={containerStyles} className="timeloom-container-group">
                    {group.layers.map(layer => (
                        <div key={layer} style={{ position: 'absolute', width: '100%', height: '100%' }} className={getLayerClassName(layer)}>
                            {renderLayerContent(layer)}
                        </div>
                    ))}
                </div>
            );
        } else if (!group.containerBased && hasEffects) {
            // PER-LAYER: Each layer gets individual effect styling
            
            const layerStyles = {
                position: 'absolute' as const,
                width: '100%',
                height: '100%',
                ...group.effectStyles
            };

            return group.layers.map(layer => (
                <div key={layer} style={layerStyles} className={getLayerClassName(layer)}>
                    {renderLayerContent(layer)}
                </div>
            ));
        } else {
            // NO EFFECTS: Render layers normally
            return group.layers.map(layer => (
                <div key={layer} style={{ position: 'absolute', width: '100%', height: '100%' }} className={getLayerClassName(layer)}>
                    {renderLayerContent(layer)}
                </div>
            ));
        }
    };

    return (
        <AbsoluteFill>
            {/* NEW HYBRID SYSTEM: Render layer groups with container-based or per-layer effects */}
            {layerGroups.map(group => renderLayerGroup(group))}
            <div className="timeloom-sound-effects">
                {/* Render all sound effects */}
                {timeline.renderSoundEffects()}
            </div>
            <div className="timeloom-main-audio">
                {/* Render main audio track */}
                {timeline.mainAudioLayer.render()}
            </div>
        </AbsoluteFill>
    );
};

// Register Timeline with the TimelineRegistry for serialization
TimelineRegistry.register('Timeline', TimelineImpl.importState);

// Register Scene and Media types with the registry
import { SceneItemImpl } from './types/scene';
import { VideoMediaImpl } from './media/VideoMedia';
import { ImageMediaImpl } from './media/ImageMedia';
import { TextMediaImpl } from './media/text';
import { GeneratedSlideImpl } from './media/GeneratedSlide';
// Note: OverlayItemImpl, TextOverlayItemImpl, CaptionItemImpl, BrandingItemImpl
// are already imported from './types' at the top of this file (lines 26-29)

TimelineRegistry.register('Scene', SceneItemImpl.importState);
TimelineRegistry.register('VideoMedia', VideoMediaImpl.importState);
TimelineRegistry.register('ImageMedia', ImageMediaImpl.importState);
TimelineRegistry.register('TextMedia', TextMediaImpl.importState);
TimelineRegistry.register('GeneratedSlide', GeneratedSlideImpl.importState);
// Overlay/text/caption/branding item classes do not yet expose typed importState methods.
// TimelineImpl.importState reconstructs those layer items directly.
